// src/services/projectClosureService.js

import {
  doc,
  getDoc,
  updateDoc,
  getDocs,
  collection,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { getAuth } from 'firebase/auth';
import collaborationService from './collaborationService';

class ProjectClosureService {
  constructor() {
    this.auth = getAuth();
  }

  /**
   * Check if user can close a project
   * @param {string} projectId - The project ID
   * @param {string} userId - The user ID (optional, uses current user if not provided)
   * @returns {Promise<{canClose: boolean, isOwner: boolean, reason?: string}>}
   */
  async canCloseProject(projectId, userId = null) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { canClose: false, isOwner: false, reason: 'User not authenticated' };
      }

      const targetUserId = userId || currentUser.uid;
      const permissions = await collaborationService.checkProjectPermissions(projectId, targetUserId);
      
      // Only project owners can close projects
      if (!permissions.isOwner) {
        return { 
          canClose: false, 
          isOwner: false, 
          reason: 'Only project owners can close projects' 
        };
      }

      return { canClose: true, isOwner: true };
    } catch (error) {
      console.error('Error checking project closure permissions:', error);
      return { canClose: false, isOwner: false, reason: 'Error checking permissions' };
    }
  }

  /**
   * Close a project and archive all teams
   * @param {string} projectId - The project ID
   * @param {Object} options - Closure options
   * @returns {Promise<{success: boolean, message: string, archivedTeams?: number}>}
   */
  async closeProject(projectId, options = {}) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Check permissions
      const canClose = await this.canCloseProject(projectId);
      if (!canClose.canClose) {
        throw new Error(canClose.reason || 'Insufficient permissions to close project');
      }

      // Get project data
      const projectRef = doc(db, 'projects', projectId);
      
      // Get all teams for this project
      const teamsQuery = collection(db, `projects/${projectId}/teams`);
      const teamsSnap = await getDocs(teamsQuery);
      const teams = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Use batch for atomic operations
      const batch = writeBatch(db);

      // 1. Update project status
      batch.update(projectRef, {
        status: 'closed',
        closedAt: serverTimestamp(),
        closedBy: currentUser.uid,
        closedByName: currentUser.displayName || currentUser.email || 'Unknown User',
        closureReason: options.reason || 'Project completed',
        archivedAt: serverTimestamp()
      });

      // 2. Archive all active teams (mark as finished if not already)
      let archivedTeamsCount = 0;
      for (const team of teams) {
        if (team.status !== 'finished') {
          const teamRef = doc(db, `projects/${projectId}/teams`, team.id);
          batch.update(teamRef, {
            status: 'finished',
            finishedAt: serverTimestamp(),
            finishedBy: currentUser.uid,
            finishedByName: currentUser.displayName || currentUser.email || 'Unknown User',
            archivedAt: serverTimestamp(),
            archiveReason: 'Project closed - team automatically finished'
          });
          archivedTeamsCount++;
        }
      }

      // 3. Revoke iPad pairing if one exists (no-op if no iPad paired)
      const pairedDeviceRef = doc(db, `projects/${projectId}/ipadConfig/pairedDevice`);
      batch.delete(pairedDeviceRef);

      // 4. Commit all changes atomically
      await batch.commit();

      console.log(`✅ Project ${projectId} closed successfully. ${archivedTeamsCount} teams archived.`);
      
      return {
        success: true,
        message: `Project closed successfully. ${archivedTeamsCount} teams were automatically finished.`,
        archivedTeams: archivedTeamsCount
      };

    } catch (error) {
      console.error('Error closing project:', error);
      return {
        success: false,
        message: error.message || 'Failed to close project. Please try again.'
      };
    }
  }

  /**
   * Reopen a closed project (only for project owners)
   * @param {string} projectId - The project ID
   * @param {Object} options - Reopening options
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async reopenProject(projectId, options = {}) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Check permissions
      const canClose = await this.canCloseProject(projectId);
      if (!canClose.canClose) {
        throw new Error(canClose.reason || 'Insufficient permissions to reopen project');
      }

      const projectRef = doc(db, 'projects', projectId);
      
      await updateDoc(projectRef, {
        status: 'active',
        reopenedAt: serverTimestamp(),
        reopenedBy: currentUser.uid,
        reopenedByName: currentUser.displayName || currentUser.email || 'Unknown User',
        reopenReason: options.reason || 'Project reopened'
      });

      console.log(`✅ Project ${projectId} reopened successfully`);
      
      return {
        success: true,
        message: 'Project reopened successfully. Teams remain in their current status.'
      };

    } catch (error) {
      console.error('Error reopening project:', error);
      return {
        success: false,
        message: error.message || 'Failed to reopen project. Please try again.'
      };
    }
  }

  /**
   * Get project closure status and team information
   * @param {string} projectId - The project ID
   * @returns {Promise<{isClosed: boolean, closedAt?: Date, closedBy?: string, teams: Array, activeTeams: number, finishedTeams: number}>}
   */
  async getProjectClosureStatus(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      
      if (!projectSnap.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectSnap.data();
      const isClosed = projectData.status === 'closed';

      // Get teams information
      const teamsQuery = collection(db, `projects/${projectId}/teams`);
      const teamsSnap = await getDocs(teamsQuery);
      const teams = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const activeTeams = teams.filter(team => team.status !== 'finished').length;
      const finishedTeams = teams.filter(team => team.status === 'finished').length;

      return {
        isClosed,
        closedAt: isClosed ? projectData.closedAt : null,
        closedBy: isClosed ? projectData.closedByName : null,
        teams,
        activeTeams,
        finishedTeams,
        totalTeams: teams.length
      };

    } catch (error) {
      console.error('Error getting project closure status:', error);
      throw error;
    }
  }
}

const projectClosureService = new ProjectClosureService();
export default projectClosureService;
